'use client';

import Script from 'next/script';
import { useSiteSettings } from '@/components/providers';
import { useServiceConsentGranted } from '@/features/consent';

/**
 * Matches the catalog entry in consent-catalog.constants.ts and is shared by
 * every tracking module so all of them hang on the same switch in the banner.
 */
export const MATOMO_SERVICE_ID = 'matomo-tag-manager';

/**
 * Loads the Matomo Tag Manager container. The component subscribes to the cookie
 * banner via the consent service: the container script is only injected once the
 * visitor has granted the "analytics" category. Configuration (enable flag and
 * container URL) comes from the Sanity siteSettings "Tracking" tab.
 */
export function MatomoTagManager() {
    const { tracking } = useSiteSettings();
    const matomo = tracking?.matomoTagManager;
    const containerUrl = matomo?.containerUrl?.trim();
    const enabled = Boolean(matomo?.enabled && containerUrl);

    // Per-service check: the visitor can switch Matomo off while Statistik stays
    // on. The banner entry comes from the catalog, gated on the same CMS flags.
    const granted = useServiceConsentGranted(MATOMO_SERVICE_ID);

    if (!enabled || !granted) return null;

    return (
        <Script id="matomo-tag-manager" strategy="afterInteractive">
            {`
                var _mtm = window._mtm = window._mtm || [];
                _mtm.push({'mtm.startTime': (new Date().getTime()), 'event': 'mtm.Start'});
                (function() {
                    var d = document, g = d.createElement('script'), s = d.getElementsByTagName('script')[0];
                    g.async = true;
                    g.src = ${JSON.stringify(containerUrl)};
                    s.parentNode.insertBefore(g, s);
                })();
            `}
        </Script>
    );
}
